import React from "react";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import SystemSelect from "../SystemSelect";
import { LivePal } from "../../types/Pal";
import {
  IoAdd,
  IoLocationOutline,
  IoCallOutline,
  IoMusicalNotes,
  IoTrashOutline,
} from "react-icons/io5";
import GearButton from "../../ui/gearbutton";
import GearInput from "../../ui/gearinput";
import { CustomCheckbox } from "../CustomCheckbox";

interface LivePalFormProps {
  registerLivePal: any;
  livePalErrors: any;
  livePalTouchedFields: any;
  isLivePalSubmitted: boolean;
  watchLivePal: any;
  setLivePalValue: any;
  triggerLivePal: any;
  isLivePalValid?: boolean;
  isAddingLivePal: boolean;
  isUpdatingLivePal?: boolean;
  isEdit?: boolean;
  palToEdit?: LivePal;
}

const LivePalForm: React.FC<LivePalFormProps> = ({
  registerLivePal,
  livePalErrors,
  livePalTouchedFields,
  isLivePalSubmitted,
  watchLivePal,
  setLivePalValue,
  triggerLivePal,
  isLivePalValid,
  isAddingLivePal,
  isUpdatingLivePal = false,
  isEdit = false,
  palToEdit = null,
}) => {
  const { t } = useTranslation();
  const equipment = watchLivePal("equipment") || [];
  const contacts = watchLivePal("contacts") || [];
  const accommodation = watchLivePal("accommodation");
  const meals = watchLivePal("meals");

  const selectStyles = {
    control: (base: any, state: any) => ({
      ...base,
      backgroundColor: "rgb(51, 24, 85)",
      borderColor: state.isFocused ? "rgba(90, 24, 196, 0.6)" : "#6e11b04d",
      borderRadius: "0.5rem",
      fontSize: "14px",
      minHeight: "40px",
      color: "white",
      boxShadow: "none",
      "&:hover": {
        borderColor: "rgba(90, 24, 196, 0.6)",
      },
    }),
    menu: (base: any) => ({
      ...base,
      backgroundColor: "#1F1235",
      borderRadius: "0.75rem",
      border: "1px solid rgba(255, 255, 255, 0.1)",
      zIndex: 100,
    }),
    option: (base: any, state: any) => ({
      ...base,
      backgroundColor: state.isSelected
        ? "#5A18C4"
        : state.isFocused
        ? "rgba(90, 24, 196, 0.2)"
        : "transparent",
      color: "white",
      cursor: "pointer",
      "&:active": {
        backgroundColor: "#5A18C4",
      },
    }),
    singleValue: (base: any) => ({
      ...base,
      color: "white",
    }),
    placeholder: (base: any) => ({
      ...base,
      color: "#6a7282 !important",
      fontSize: "14px !important",
    }),
    input: (base: any) => ({
      ...base,
      color: "white",
    }),
    indicatorSeparator: () => ({
      display: "none",
    }),
    dropdownIndicator: (base: any) => ({
      ...base,
      color: "rgba(255, 255, 255, 0.4)",
      "&:hover": {
        color: "white",
      },
    }),
  };

  return (
    <div className="space-y-8">
      {/* Informazioni Generali */}
      <div className="space-y-4">
        <GearInput
          {...registerLivePal("name")}
          label={t("forms.venueName", "Nome del Locale")}
          placeholder={t("forms.venueName", "Nome del Locale")}
          error={livePalErrors.name?.message}
        />

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <GearInput
            type="number"
            {...registerLivePal("capacity", { valueAsNumber: true })}
            label={t("forms.capacity", "Capacità")}
            placeholder="100"
            error={livePalErrors.capacity?.message}
          />
          <div className="flex flex-col mb-4">
            <label className="block text-white text-[14px] font-[400] mb-1">
              {t("forms.venueType", "Tipo di Locale")}
            </label>
            <div className="flex gap-4 h-[42px] items-center">
              <label className="flex items-center gap-2 text-white cursor-pointer group">
                <div
                  className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
                    watchLivePal("venueType") === "acoustic"
                      ? "border-secondary bg-secondary"
                      : "border-white/20"
                  }`}
                >
                  {watchLivePal("venueType") === "acoustic" && (
                    <div className="w-2 rounded-full bg-white h-2" />
                  )}
                </div>
                <input
                  type="radio"
                  className="hidden"
                  onChange={() => setLivePalValue("venueType", "acoustic")}
                  checked={watchLivePal("venueType") === "acoustic"}
                />
                <span className="text-sm">
                  {t("filters.acoustic", "Acoustic")}
                </span>
              </label>
              <label className="flex items-center gap-2 text-white cursor-pointer group">
                <div
                  className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
                    watchLivePal("venueType") === "fullBand"
                      ? "border-secondary bg-secondary"
                      : "border-white/20"
                  }`}
                >
                  {watchLivePal("venueType") === "fullBand" && (
                    <div className="w-2 rounded-full bg-white h-2" />
                  )}
                </div>
                <input
                  type="radio"
                  className="hidden"
                  onChange={() => setLivePalValue("venueType", "fullBand")}
                  checked={watchLivePal("venueType") === "fullBand"}
                />
                <span className="text-sm">
                  {t("filters.fullBand", "Full Band")}
                </span>
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* Località */}
      <div className="p-5 rounded-2xl border border-[#451671] bg-[#3A1A60] space-y-4">
        <h4 className="text-secondary font-semibold flex items-center gap-2 mb-2">
          <IoLocationOutline size={20} className="text-primary" />{" "}
          {t("forms.location", "Località")}
        </h4>
        <GearInput
          {...registerLivePal("address")}
          label={t("forms.address", "Indirizzo")}
          placeholder="Via/Piazza..."
          error={livePalErrors.address?.message}
        />
        <div className="grid grid-cols-2 gap-4">
          <GearInput
            {...registerLivePal("zipCode")}
            label={t("forms.zipCode", "CAP")}
            placeholder="12345"
            error={livePalErrors.zipCode?.message}
          />
          <GearInput
            {...registerLivePal("city")}
            label={t("forms.city", "Città")}
            placeholder="Roma"
            error={livePalErrors.city?.message}
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <GearInput
            {...registerLivePal("region")}
            label={t("forms.region", "Regione")}
            placeholder="Lazio"
            error={livePalErrors.region?.message}
          />
          <GearInput
            {...registerLivePal("country")}
            label={t("forms.country", "Paese")}
            placeholder="Italy"
            error={livePalErrors.country?.message}
          />
        </div>
      </div>

      {/* Contatti */}
      <div className="p-5 rounded-2xl border border-[#451671] bg-[#3A1A60] space-y-4">
        <h4 className="text-secondary font-semibold flex items-center gap-2 mb-2">
          <IoCallOutline size={20} className="text-primary" />{" "}
          {t("forms.contacts", "Contatti")}
        </h4>
        <div className="grid grid-cols-1 gap-4">
          <GearInput
            {...registerLivePal("email")}
            label={t("forms.email", "Email")}
            placeholder="info@venue.com"
            error={livePalErrors.email?.message}
          />
          <GearInput
            {...registerLivePal("website")}
            label={t("forms.website", "Sito Web")}
            placeholder="https://example.com"
            error={livePalErrors.website?.message}
          />
        </div>

        <div className="space-y-4 pt-2">
          <p className="text-xs text-secondary/70">
            {t(
              "forms.extraContactsHint",
              "Aggiungi altri social o riferimenti"
            )}
          </p>
          {contacts.map((contact: any, index: number) => (
            <div
              key={index}
              className="flex gap-2 items-start bg-[#1F1235]/50 p-3 rounded-lg border border-white/5"
            >
              <div className="flex-grow space-y-3">
                <div className="isMultiSelect">
                  <Select
                    styles={selectStyles}
                    options={[
                      { value: "email", label: "EMAIL" },
                      { value: "instagram", label: "INSTAGRAM" },
                      { value: "x", label: "X" },
                      { value: "linkedin", label: "LINKEDIN" },
                      { value: "facebook", label: "FACEBOOK" },
                      { value: "website", label: "WEBSITE" },
                      { value: "telegram", label: "TELEGRAM" },
                    ]}
                    value={[
                      { value: "email", label: "EMAIL" },
                      { value: "instagram", label: "INSTAGRAM" },
                      { value: "x", label: "X" },
                      { value: "linkedin", label: "LINKEDIN" },
                      { value: "facebook", label: "FACEBOOK" },
                      { value: "website", label: "WEBSITE" },
                      { value: "telegram", label: "TELEGRAM" },
                    ].find((o) => o.value === contact.type)}
                    onChange={(val: any) => {
                      const newContacts = [...contacts];
                      newContacts[index] = { ...contact, type: val.value };
                      setLivePalValue("contacts", newContacts);
                    }}
                    isSearchable={false}
                  />
                </div>
                <GearInput
                  {...registerLivePal(`contacts.${index}.value`)}
                  placeholder={t("forms.value", "Valore")}
                  containerClassName="mb-0"
                  error={livePalErrors.contacts?.[index]?.value?.message}
                />
              </div>
              <button
                type="button"
                onClick={() => {
                  const newContacts = contacts.filter(
                    (_: any, i: number) => i !== index
                  );
                  setLivePalValue("contacts", newContacts);
                }}
                className="mt-2.5 p-2 bg-red-500/10 text-red-500 rounded-full hover:bg-red-500/20 transition-colors"
              >
                <IoTrashOutline size={18} />
              </button>
            </div>
          ))}
          <GearButton
            type="button"
            variant="ghost"
            onClick={() => {
              const newContacts = [...contacts, { type: "email", value: "" }];
              setLivePalValue("contacts", newContacts);
            }}
            className="w-full !border-dashed !border-white/20 !bg-transparent hover:!bg-white/5"
          >
            <div className="flex items-center gap-2 justify-center py-1 text-white">
              <IoAdd /> {t("forms.addContact", "Aggiungi Contatto")}
            </div>
          </GearButton>
        </div>
      </div>

      {/* Servizi & Equipment */}
      <div className="p-5 rounded-2xl border border-[#451671] bg-[#3A1A60] space-y-4">
        <h4 className="text-secondary font-semibold flex items-center gap-2 mb-2">
          <IoMusicalNotes size={20} className="text-primary" />{" "}
          {t("forms.servicesAndEquipment", "Servizi e Attrezzatura")}
        </h4>
        <div className="flex gap-8 py-2">
          <CustomCheckbox
            {...registerLivePal("accommodation")}
            checked={accommodation}
            label={t("filters.accommodation", "Alloggio")}
          />
          <CustomCheckbox
            {...registerLivePal("meals")}
            checked={meals}
            label={t("filters.meals", "Pasti")}
          />
        </div>

        <div className="mt-4 pt-2 border-t border-white/5">
          <label className="block text-white text-[14px] font-[400] mb-3">
            {t("forms.availableEquipment", "Attrezzatura Disponibile")}
          </label>
          <SystemSelect
            value={equipment}
            onChange={(ids: any) => setLivePalValue("equipment", ids)}
            useTranslationKeys={true}
          />
        </div>
      </div>

      {/* Submit Buttons */}
      <div className="flex justify-end pt-4">
        {!isEdit ? (
          <GearButton
            type="submit"
            variant="secondary"
            disabled={!isLivePalValid || isAddingLivePal}
            className="w-full !rounded-full  shadow-[0_0_20px_rgba(90,24,196,0.3)] hover:shadow-[0_0_30px_rgba(90,24,196,0.5)] transition-all"
          >
            <div className="flex items-center gap-2 justify-center">
              <IoAdd size={22} />
              <span className="text-sm mt-[-2px]">
                {isAddingLivePal
                  ? t("forms.submitting", "Invio in corso...")
                  : t("forms.addVenueButton", "Crea Locale")}
              </span>
            </div>
          </GearButton>
        ) : (
          <GearButton
            type="submit"
            variant="secondary"
            disabled={!isLivePalValid || isUpdatingLivePal}
            className="w-full !rounded-full shadow-[0_0_20px_rgba(90,24,196,0.3)] hover:shadow-[0_0_30px_rgba(90,24,196,0.5)] transition-all"
          >
            <div className="flex items-center gap-2 justify-center">
              <span className=" text-[14px]">
                {isUpdatingLivePal
                  ? t("forms.updating", "Aggiornamento in corso...")
                  : t("forms.saveChanges", "Salva Modifiche")}
              </span>
            </div>
          </GearButton>
        )}
      </div>
    </div>
  );
};

export default LivePalForm;
